import { reportClientError } from "./client-errors.functions";

const RECENT: Map<string, number> = new Map();
const DEDUPE_MS = 10_000;
const MAX_PER_MINUTE = 20;
let windowStart = Date.now();
let countInWindow = 0;

function shouldSend(key: string): boolean {
  const now = Date.now();
  if (now - windowStart > 60_000) {
    windowStart = now;
    countInWindow = 0;
  }
  if (countInWindow >= MAX_PER_MINUTE) return false;
  const last = RECENT.get(key);
  if (last && now - last < DEDUPE_MS) return false;
  RECENT.set(key, now);
  countInWindow++;
  // Trim old entries
  if (RECENT.size > 100) {
    for (const [k, t] of RECENT) {
      if (now - t > DEDUPE_MS) RECENT.delete(k);
    }
  }
  return true;
}

function send(payload: {
  level: "error" | "console_error" | "unhandled_rejection";
  message: string;
  stack?: string | null;
  extra?: Record<string, unknown> | null;
}) {
  const key = `${payload.level}:${payload.message}`;
  if (!shouldSend(key)) return;
  try {
    void reportClientError({
      data: {
        level: payload.level,
        message: payload.message.slice(0, 2000),
        stack: payload.stack ? String(payload.stack).slice(0, 8000) : null,
        url: typeof window !== "undefined" ? window.location.href.slice(0, 2000) : null,
        user_agent:
          typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : null,
        extra: payload.extra ?? null,
      },
    }).catch(() => {});
  } catch {
    // swallow — never let reporter break the app
  }
}

let installed = false;
export function installClientErrorReporter() {
  if (installed) return;
  if (typeof window === "undefined") return;
  // Only enable in production builds to avoid noise during dev/HMR
  if (import.meta.env.DEV) return;
  installed = true;

  window.addEventListener("error", (event) => {
    const err = event.error;
    send({
      level: "error",
      message: err?.message || event.message || "Unknown error",
      stack: err?.stack ?? null,
      extra: {
        filename: event.filename,
        lineno: event.lineno,
        colno: event.colno,
      },
    });
  });

  window.addEventListener("unhandledrejection", (event) => {
    const reason = event.reason;
    const message =
      reason instanceof Error
        ? reason.message
        : typeof reason === "string"
        ? reason
        : JSON.stringify(reason)?.slice(0, 2000) || "Unhandled promise rejection";
    send({
      level: "unhandled_rejection",
      message,
      stack: reason instanceof Error ? reason.stack ?? null : null,
    });
  });

  const originalConsoleError = console.error.bind(console);
  console.error = (...args: unknown[]) => {
    try {
      const message = args
        .map((a) =>
          a instanceof Error ? a.message : typeof a === "string" ? a : safeStringify(a),
        )
        .join(" ")
        .slice(0, 2000);
      const stack = args.find((a) => a instanceof Error) as Error | undefined;
      send({
        level: "console_error",
        message,
        stack: stack?.stack ?? null,
      });
    } catch {
      // ignore
    }
    originalConsoleError(...args);
  };
}

function safeStringify(v: unknown): string {
  try {
    return JSON.stringify(v);
  } catch {
    return String(v);
  }
}